export type RecencyTone = 'green' | 'amber' | 'red';

export interface RecencyDisplay {
  text: string; // "Today" | "1d ago" | "12d ago" | "Never"
  tone: RecencyTone;
}

const THRESHOLDS: Record<'visit' | 'touch', { green: number; amber: number }> = {
  visit: { green: 30, amber: 90 },
  touch: { green: 14, amber: 45 },
};

/** Whole days elapsed between an ISO timestamp and `now` (floored). */
export function daysSince(iso: string, now: Date = new Date()): number {
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

export function recencyDisplay(
  iso: string | null,
  kind: 'visit' | 'touch',
  now: Date = new Date(),
): RecencyDisplay {
  if (!iso) return { text: 'Never', tone: 'red' };
  const d = daysSince(iso, now);
  const t = THRESHOLDS[kind];
  const tone: RecencyTone = d <= t.green ? 'green' : d <= t.amber ? 'amber' : 'red';
  const text = d <= 0 ? 'Today' : d === 1 ? '1d ago' : `${d}d ago`;
  return { text, tone };
}

export function formatDollars(cents: string): string {
  const dollars = Number(BigInt(cents)) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars);
}

/** Compact money for dashboards: $6.30M / $29.4K / $520. Input is a cents string. */
export function formatDollarsCompact(cents: string): string {
  const dollars = Number(BigInt(cents)) / 100;
  const abs = Math.abs(dollars);
  if (abs >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(dollars / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars);
}

/** Current school-year key in the datamart's 'YYYY-YY' format (Jul 1 boundary, UTC).
 *  Mirrors readDonationOrgPeriodRollups' sy_key so OrgSnapshot selects the
 *  authoritative CURRENT-SY OrgDonationRollup row. */
export function currentSchoolYearKey(now: Date = new Date()): string {
  const syStart = now.getUTCMonth() >= 6 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
  return `${syStart}-${String((syStart + 1) % 100).padStart(2, '0')}`;
}
